/**
 * SAML / Entra External ID email-claim diagnostic helpers.
 *
 * Microsoft Entra External ID (CIAM) surfaces AADSTS5000819 when the
 * SAML assertion from a federated identity provider does not carry an
 * acceptable email address claim:
 *
 *   "SAML Assertion is invalid. Email address claim is missing or does
 *    not match domain from an external realm."
 *
 * Even though this portal normally consumes OIDC ID tokens (not raw
 * SAML assertions), the claim set Entra forwards to the SPA frequently
 * carries the original SAML claim URIs. These helpers let the Tokens
 * page render a structured diagnostic so an operator can see at a
 * glance which claim (if any) Entra could use as the email identifier
 * and whether its domain matches the configured external realm.
 */

/** A single claim we will probe on the token payload. */
export type ClaimProbe = {
  /** Human-friendly label used in the diagnostic table. */
  name: string;
  /**
   * All candidate payload keys to try, in order. The first non-empty
   * value wins. Multiple keys are needed because Entra may emit the
   * same logical claim under different names depending on the IdP, the
   * claim-mapping policy, and whether optional claims are enabled.
   */
  keys: string[];
  /**
   * Semantic category — determines how the value is treated when we
   * compute the "final detected email".
   */
  kind: "email" | "nameid" | "subject" | "other";
};

/**
 * Canonical list of claim names the user's runbook requires us to
 * check. Keep this in the order stipulated so the rendered table is
 * predictable for the support engineer reading it.
 */
export const CLAIM_PROBES: ClaimProbe[] = [
  {
    name: "emailaddress",
    keys: ["emailaddress"],
    kind: "email",
  },
  {
    name: "email",
    keys: ["email"],
    kind: "email",
  },
  {
    name: "mail",
    keys: ["mail"],
    kind: "email",
  },
  {
    name: "userPrincipalName",
    keys: ["userPrincipalName"],
    kind: "email",
  },
  {
    name: "upn",
    keys: ["upn"],
    kind: "email",
  },
  {
    name: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
    keys: [
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
    ],
    kind: "email",
  },
  {
    name: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name",
    keys: ["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
    kind: "other",
  },
  {
    name: "NameID",
    keys: ["NameID", "nameID", "nameId"],
    kind: "nameid",
  },
  {
    name: "subject",
    keys: ["subject", "sub"],
    kind: "subject",
  },
];

/** `true` if the value is null, undefined, or a whitespace-only string. */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Extremely permissive email shape test — we cannot afford to reject
 * legitimate internationalised addresses, but we do need to distinguish
 * an email from an opaque NameID like `persistent-a1b2c3d4`.
 */
export function looksLikeEmail(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.length < 3) return false;
  if (trimmed.includes(" ")) return false;
  const at = trimmed.indexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return false;
  if (trimmed.lastIndexOf("@") !== at) return false;
  const domain = trimmed.slice(at + 1);
  return domain.includes(".") && !domain.endsWith(".");
}

/** Return the domain portion (after the `@`) of an email-like string. */
export function getDomainFromEmail(email: unknown): string | null {
  if (typeof email !== "string") return null;
  const at = email.indexOf("@");
  if (at < 0) return null;
  const domain = email.slice(at + 1).trim();
  return domain.length ? domain : null;
}

/**
 * Lower-case + strip surrounding whitespace + trailing dot (FQDN form).
 * Does NOT strip subdomains — a mismatch between `eu.contoso.com` and
 * `contoso.com` is a real mismatch we want to surface.
 */
export function normalizeDomain(domain: unknown): string {
  if (typeof domain !== "string") return "";
  return domain.trim().toLowerCase().replace(/\.$/, "");
}

/**
 * Compare two domains after normalisation.
 * Returns `"match"`, `"mismatch"`, or `"unknown"` when either side is
 * missing (we cannot meaningfully decide).
 */
export function compareDomains(
  emailDomain: unknown,
  expectedDomain: unknown,
): "match" | "mismatch" | "unknown" {
  const a = normalizeDomain(emailDomain);
  const b = normalizeDomain(expectedDomain);
  if (!a || !b) return "unknown";
  return a === b ? "match" : "mismatch";
}

/* ------------------------------------------------------------------ */
/*  Diagnostic model                                                   */
/* ------------------------------------------------------------------ */

export type ClaimStatus = "present" | "missing" | "empty";

export type ClaimRow = {
  name: string;
  /** First key that resolved to a non-empty value (or the first probe key). */
  resolvedKey: string;
  value: string | null;
  status: ClaimStatus;
  kind: ClaimProbe["kind"];
};

export type CapturedAuthError = {
  errorCode: string | null;
  errorMessage: string | null;
  traceId: string | null;
  correlationId: string | null;
  timestamp: string | null;
  subError: string | null;
  raw: unknown;
};

export type DomainCheck =
  | { kind: "match"; actual: string; expected: string }
  | { kind: "mismatch"; actual: string; expected: string }
  | { kind: "no-email" }
  | { kind: "no-expected" };

export type SamlEmailDiagnostic = {
  error: CapturedAuthError;
  claims: ClaimRow[];
  detectedEmail: string | null;
  detectedEmailSource: string | null;
  detectedDomain: string | null;
  expectedDomain: string | null;
  domainCheck: DomainCheck;
  /** Raw NameID from claims for display; the literal string `"empty"` when missing or blank. */
  nameIdDisplay: string;
  nameIdIsEmail: boolean;
  summary: string;
  suggestedFix: string;
};

/* ------------------------------------------------------------------ */
/*  Builder                                                            */
/* ------------------------------------------------------------------ */

function firstNonEmpty(
  payload: Record<string, unknown> | null | undefined,
  keys: string[],
): { key: string; value: string | null; status: ClaimStatus } {
  if (!payload) {
    return { key: keys[0] ?? "", value: null, status: "missing" };
  }
  for (const key of keys) {
    if (!(key in payload)) continue;
    const raw = payload[key];
    if (isEmpty(raw)) {
      return { key, value: null, status: "empty" };
    }
    if (Array.isArray(raw)) {
      const firstString = raw.find((v) => typeof v === "string" && v.length);
      if (typeof firstString === "string") {
        return { key, value: firstString, status: "present" };
      }
      return { key, value: JSON.stringify(raw), status: "present" };
    }
    return { key, value: String(raw), status: "present" };
  }
  return { key: keys[0] ?? "", value: null, status: "missing" };
}

/**
 * Parse an MSAL error (or any plain object / string) into our
 * canonical CapturedAuthError shape. Extracts AADSTS code, Trace ID,
 * Correlation ID, and Timestamp from the standard Microsoft error
 * message so they can be rendered individually.
 */
export function parseAuthError(raw: unknown): CapturedAuthError {
  const empty: CapturedAuthError = {
    errorCode: null,
    errorMessage: null,
    traceId: null,
    correlationId: null,
    timestamp: null,
    subError: null,
    raw,
  };
  if (raw === null || raw === undefined) return empty;

  const obj = (typeof raw === "object" ? (raw as Record<string, unknown>) : {}) ?? {};
  const message =
    typeof raw === "string"
      ? raw
      : typeof obj.errorMessage === "string"
        ? (obj.errorMessage as string)
        : typeof obj.message === "string"
          ? (obj.message as string)
          : null;

  const errorCode =
    typeof obj.errorCode === "string" && (obj.errorCode as string).length
      ? (obj.errorCode as string)
      : (message?.match(/AADSTS\d+/)?.[0] ?? null);

  const correlationId =
    typeof obj.correlationId === "string"
      ? (obj.correlationId as string)
      : (message?.match(/Correlation ID:\s*([a-f0-9-]+)/i)?.[1] ?? null);

  const traceId = message?.match(/Trace ID:\s*([a-f0-9-]+)/i)?.[1] ?? null;

  const timestamp =
    message?.match(/Timestamp:\s*([0-9T:\-.Z ]+)/i)?.[1]?.trim() ?? null;

  const subError =
    typeof obj.subError === "string" ? (obj.subError as string) : null;

  return {
    errorCode,
    errorMessage: message,
    traceId,
    correlationId,
    timestamp,
    subError,
    raw,
  };
}

/**
 * Compute the end-to-end SAML email diagnostic shown on the Tokens
 * page. This function is pure — all UI concerns live in the React
 * component that consumes the returned value.
 */
export function buildSamlEmailDiagnostic(params: {
  /** Decoded ID-token payload (OIDC claims), or `null` if the user is not signed in. */
  claims: Record<string, unknown> | null | undefined;
  /** MSAL account.username — used as an additional email candidate when present. */
  accountUsername?: string | null;
  /** Expected external realm domain from runtime config (may be empty). */
  expectedEmailDomain: string;
  /** Most recent MSAL / redirect error captured for the current session. */
  lastError: unknown;
}): SamlEmailDiagnostic {
  const { claims, accountUsername, expectedEmailDomain, lastError } = params;

  const error = parseAuthError(lastError);

  // Probe every requested claim regardless of whether the preceding
  // one matched — the support engineer wants the full picture.
  const claimRows: ClaimRow[] = CLAIM_PROBES.map((probe) => {
    const hit = firstNonEmpty(claims ?? null, probe.keys);
    return {
      name: probe.name,
      resolvedKey: hit.key,
      value: hit.value,
      status: hit.status,
      kind: probe.kind,
    };
  });

  // Order of precedence for the "final detected email".
  // Matches the spec: emailaddress → email → mail → userPrincipalName
  // → NameID (only if it looks like an email).
  const pickByName = (name: string): ClaimRow | undefined =>
    claimRows.find((r) => r.name === name);

  const candidates: Array<{ source: string; value: string | null }> = [
    { source: "emailaddress", value: pickByName("emailaddress")?.value ?? null },
    { source: "email", value: pickByName("email")?.value ?? null },
    { source: "mail", value: pickByName("mail")?.value ?? null },
    {
      source: "userPrincipalName",
      value: pickByName("userPrincipalName")?.value ?? null,
    },
  ];

  // MSAL exposes account.username which Entra populates from
  // preferred_username — not in the required list but worth falling
  // back to BEFORE NameID, because NameID is often opaque.
  if (accountUsername && looksLikeEmail(accountUsername)) {
    candidates.push({ source: "account.username", value: accountUsername });
  }

  const nameIdClaim = pickByName("NameID");
  const nameIdValue = nameIdClaim?.value ?? null;
  const nameIdDisplay =
    nameIdClaim?.status === "present" &&
    typeof nameIdValue === "string" &&
    nameIdValue.trim().length > 0
      ? nameIdValue.trim()
      : "empty";
  const nameIdIsEmail = looksLikeEmail(nameIdValue);
  if (nameIdIsEmail) {
    candidates.push({ source: "NameID", value: nameIdValue });
  }

  const picked = candidates.find(
    (c) => typeof c.value === "string" && c.value.length > 0,
  );
  const detectedEmail = picked?.value ?? null;
  const detectedEmailSource = picked?.source ?? null;
  const detectedDomain = getDomainFromEmail(detectedEmail);

  const expected = normalizeDomain(expectedEmailDomain);
  const expectedDomain = expected.length ? expected : null;

  let domainCheck: DomainCheck;
  if (!detectedEmail) {
    domainCheck = { kind: "no-email" };
  } else if (!expectedDomain) {
    domainCheck = { kind: "no-expected" };
  } else {
    const cmp = compareDomains(detectedDomain, expectedDomain);
    domainCheck =
      cmp === "match"
        ? { kind: "match", actual: detectedDomain!, expected: expectedDomain }
        : { kind: "mismatch", actual: detectedDomain ?? "", expected: expectedDomain };
  }

  // Build the summary + suggested fix using the same phrasing the
  // runbook prescribes.
  let summary: string;
  let suggestedFix: string;

  if (!detectedEmail) {
    summary =
      "Missing email claim. Entra External ID requires an email address claim or mapped identifier that matches the configured external realm/domain.";
    suggestedFix =
      "Update the federated IdP to send an `emailaddress` (or `email`/`mail`/`upn`) claim in the SAML assertion, OR configure an Entra claim-mapping policy that maps the existing claim to `emailaddress`. Verify that the user's home IdP actually has an email on the account.";
  } else if (domainCheck.kind === "mismatch") {
    summary = `Email domain mismatch. The SAML assertion provided email domain \`${domainCheck.actual || "(empty)"}\`, but Entra External ID expected \`${domainCheck.expected}\`.`;
    suggestedFix = `Either update Entra External ID's external-realm/federation settings to accept \`${domainCheck.actual || "the actual domain"}\`, or fix the IdP so the email claim uses the \`${domainCheck.expected}\` domain. Do not add the external realm to both sides — pick one.`;
  } else if (nameIdIsEmail && detectedEmailSource === "NameID" && !pickByName("emailaddress")?.value && !pickByName("email")?.value) {
    summary =
      "NameID is not an email address (or is being used instead of the email claim). If Entra External ID is using NameID as the sign-in identifier, the IdP may need to send email as NameID or Entra must be configured to map the emailaddress claim correctly.";
    suggestedFix =
      "In the IdP, set NameID Format to `urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress` and emit the user's email as the NameID; OR in Entra, add an additional `emailaddress` claim via claim-mapping.";
  } else if (domainCheck.kind === "no-expected") {
    summary =
      "Email claim appears present but the expected external realm/domain is not configured in this app, so the domain cannot be validated here.";
    suggestedFix =
      "Set VITE_EXPECTED_EMAIL_DOMAIN (wrangler.jsonc → vars or .env.local) to the domain Entra's external-realm / federation is configured with, then reload this page. This lets the diagnostic flag mismatches automatically.";
  } else {
    summary =
      "Email claim appears present and domain matches. Investigate claim mapping, NameID format, issuer/audience, or external identity provider configuration.";
    suggestedFix =
      "Compare the IdP's SAML metadata (issuer + EntityID + certificate) with the Entra External ID federation settings, verify the signing certificate has not rolled, and confirm the assertion audience matches Entra's expected audience. Re-run the sign-in with SAML-tracer to capture the raw assertion.";
  }

  return {
    error,
    claims: claimRows,
    detectedEmail,
    detectedEmailSource,
    detectedDomain,
    expectedDomain,
    domainCheck,
    nameIdDisplay,
    nameIdIsEmail,
    summary,
    suggestedFix,
  };
}

/* ------------------------------------------------------------------ */
/*  sessionStorage persistence for the last auth error                 */
/* ------------------------------------------------------------------ */

const AUTH_ERROR_KEY = "portal:lastAuthError";

/** Shape we persist (JSON-safe). */
export type StoredAuthError = {
  errorCode?: string | null;
  errorMessage?: string | null;
  correlationId?: string | null;
  subError?: string | null;
  stack?: string | null;
  capturedAt: string;
};

export function persistAuthError(err: unknown): void {
  try {
    const e = err as Record<string, unknown> | null | undefined;
    const payload: StoredAuthError = {
      errorCode:
        typeof e?.errorCode === "string" ? (e.errorCode as string) : null,
      errorMessage:
        typeof e?.errorMessage === "string"
          ? (e.errorMessage as string)
          : typeof e?.message === "string"
            ? (e.message as string)
            : typeof err === "string"
              ? err
              : null,
      correlationId:
        typeof e?.correlationId === "string"
          ? (e.correlationId as string)
          : null,
      subError: typeof e?.subError === "string" ? (e.subError as string) : null,
      stack: typeof e?.stack === "string" ? (e.stack as string) : null,
      capturedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(AUTH_ERROR_KEY, JSON.stringify(payload));
  } catch {
    /* sessionStorage can throw in private-mode / quota cases — ignore. */
  }
}

export function readStoredAuthError(): StoredAuthError | null {
  try {
    const raw = sessionStorage.getItem(AUTH_ERROR_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuthError;
  } catch {
    return null;
  }
}

export function clearStoredAuthError(): void {
  try {
    sessionStorage.removeItem(AUTH_ERROR_KEY);
  } catch {
    /* ignore */
  }
}
